// Pure derivation of "Open items" - everything an NJRC staffer needs to see
// at a glance about what still needs to be taken care of for this person.
//
// Aggregates across:
//   • MCCS records - open-status cases, time-payment balances, active
//     warrants. Pulled from RecordRow.data (summary + subPages).
//   • External MVC lookups - outstanding NJ surcharge debt and the
//     restoration-fee balance. Pulled from latest external_lookups for
//     mvc-surcharge / mvc-restoration.
//   • Upcoming court dates - already computed server-side; passed in.
//
// All logic is heuristic on field names + text values; the underlying
// court site has no normalized "balance" or "warrant" flag column. We're
// careful to only count things we're confident about - a missing or
// ambiguous field becomes "unknown" rather than $0, so the UI can show
// "amount uncertain" instead of falsely claiming $0 owed.
//
// Pure module - no React, no fetch. Imported by OpenItemsSummary
// (rendering) and could be unit-tested independently if needed.

import type { RecordRow, RecordData, UpcomingCourtDate } from "../types";
import type { ExternalLookup } from "../components/ExternalSourcesPanel";
import { isOpenStatus, recordStatus } from "./fieldGroups";

// ─────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────

export type OpenItemKind =
  | "mccs-fines"
  | "mccs-warrant"
  | "mvc-surcharge"
  | "mvc-restoration"
  | "court-date"
  | "payment-due";

export type ItemUrgency = "high" | "med" | "low";

/** Where clicking an open-items row takes you. The component dispatches
 *  through PersonPage so each variant can do the right thing - switching
 *  the view mode, toggling filters, scrolling with offset for the sticky
 *  header. Plain `<a href="#anchor">` doesn't cut it: most targets only
 *  exist when the cards view is active, and the open-only filter has to
 *  be flipped on for MCCS fines to actually be visible at the destination. */
export type OpenItemLink =
  /** "MCCS time-payment balances" header click. Switches to the cards
   *  view, turns ON the open-only filter, and scrolls to the records
   *  section so only the cases with balances are visible. */
  | { kind: "open-records" }
  /** MVC surcharge / restoration card. Ensures cards view (the only
   *  view where ExternalSourcesPanel renders), scrolls to that source's
   *  card. */
  | { kind: "mvc-source"; source: "mvc-surcharge" | "mvc-restoration" }
  /** A specific record card - used for warrants and upcoming-date
   *  items. Switches to cards view, scrolls + flashes the matching
   *  card. Same behavior as ExpungementSection's own onJumpToRecord. */
  | { kind: "record"; recordId: number };

/** A single line item in the "Open items" section. The component renders
 *  these as a stacked list with icon + label + value + chevron. */
export interface OpenItem {
  kind: OpenItemKind;
  /** Short, scannable label - the bold left-side text. */
  label: string;
  /** Right-side value - usually a dollar amount or date phrase. */
  value: string;
  /** Subtle one-liner under the label giving context (case#, court, etc). */
  detail?: string;
  /** Click target - see OpenItemLink. null = non-clickable row. */
  link: OpenItemLink | null;
  urgency: ItemUrgency;
  /** When the item represents money, the dollar amount. Used to feed the
   *  combined "total to resolve" headline metric. */
  amount: number | null;
}

export interface OpenItemsResult {
  /** Combined dollar total across every item we have a confident amount
   *  for. Excludes items where the amount couldn't be parsed. */
  totalDollars: number;
  /** True when at least one source has fines but the amount couldn't be
   *  parsed - drives a "+ unparsed amounts" hint next to the total. */
  hasUnknownAmounts: boolean;
  /** Total count of items needing attention. Used for the section
   *  header ("3 items needing attention"). */
  itemCount: number;
  /** All line items, ordered by urgency (high first), then by amount
   *  descending within the same urgency. */
  items: OpenItem[];
  /** The closest upcoming court date, hoisted out for the headline tile. */
  nextCourtDate: UpcomingCourtDate | null;
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers - record-level field picking
// ─────────────────────────────────────────────────────────────────────────

function isRecordData(x: unknown): x is RecordData {
  return (
    !!x &&
    typeof x === "object" &&
    "summary" in (x as Record<string, unknown>) &&
    typeof (x as { summary: unknown }).summary === "object"
  );
}

function pickStr(s: Record<string, string | string[]> | undefined, key: string): string {
  if (!s) return "";
  const v = s[key];
  if (!v) return "";
  return Array.isArray(v) ? v[v.length - 1] ?? "" : v;
}

/** Parse a "$1,234.56" string into a number. Returns null when no
 *  amount is found - distinct from $0.00 (which is a real "no balance"). */
function parseDollars(raw: string): number | null {
  if (!raw) return null;
  const m = raw.match(/\$?\s*([\d,]+(?:\.\d{1,2})?)/);
  if (!m) return null;
  const n = parseFloat(m[1]!.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

/** Richer per-record balance info. Includes the dollar amount AND a
 *  "collection-account signature" - a stable string built from
 *  (court · balance · assessed · paid) that lets the caller detect when
 *  MCCS is displaying the SAME rolled-up balance on multiple ticket rows.
 *
 *  Why we need the signature: NJ municipal parking-ticket collections
 *  roll all of a defendant's unpaid tickets at one court into a single
 *  collection account, then display that account's CUMULATIVE Assessed
 *  Total / Paid Total / Balance Due on every individual ticket row. So
 *  if Alfredo Tarquino has 30 unpaid parking tickets at Jersey City Muni
 *  with a $498 collection balance, all 30 ticket rows show "$498 Balance
 *  Due". Without de-duplication we'd report 30 × $498 = $14,940 owed,
 *  which is 30× the actual debt. The signature lets the aggregator group
 *  records that share the same rolled-up balance and only count it once.
 *
 *  Returns null when no balance is parseable. The caller decides how to
 *  interpret 0 (we treat it as "amount known + nothing owed", different
 *  from "data missing"). */
export interface RecordBalanceInfo {
  amount: number;
  /** Court code prefix, e.g. "0906" - the bit before the dash in the
   *  Court field. Used in the signature so two unrelated $498 balances at
   *  different courts don't collapse. */
  courtKey: string;
  /** Stable key identifying the collection account this record belongs
   *  to. Records sharing this key represent a SINGLE collection-account
   *  balance shown on multiple tickets; count the balance once. */
  signature: string;
}

export function recordBalanceInfo(rec: RecordRow): RecordBalanceInfo | null {
  if (!isRecordData(rec.data)) return null;
  // Field bag order: subPages (where Time Payment / Collection fields
  // live in production) before summary. We pick the FIRST parseable
  // value for each field - subPage data is more reliable when present.
  const fieldBags: Array<Record<string, string | string[]>> = [];
  for (const sp of rec.data.subPages ?? []) {
    if (sp.fields) fieldBags.push(sp.fields);
  }
  fieldBags.push(rec.data.summary);
  let amount: number | null = null;
  let assessed: number | null = null;
  let paid: number | null = null;
  const balanceCandidates = ["Balance Due", "Total Balance", "Outstanding Balance", "Amount Due"];
  for (const bag of fieldBags) {
    if (amount === null) {
      for (const key of balanceCandidates) {
        const n = parseDollars(pickStr(bag, key));
        if (n !== null) { amount = n; break; }
      }
    }
    if (assessed === null) assessed = parseDollars(pickStr(bag, "Assessed Total"));
    if (paid === null) paid = parseDollars(pickStr(bag, "Paid Total"));
  }
  if (amount === null) return null;
  // Court key - e.g. "0906 - JERSEY CITY MUNI" → "0906". Falls back to the
  // raw string when no dash is present.
  const courtRaw = pickStr(rec.data.summary, "Court");
  const courtKey = courtRaw.split(/\s*-\s*/)[0]?.trim() || courtRaw;
  // Include all three numbers in the signature so the heuristic is
  // conservative - three independent matches by accident is vanishingly
  // unlikely. (Just balance alone could over-collapse; e.g. two unrelated
  // $50 standard-fine tickets at the same court.)
  const signature = `${courtKey}|${amount.toFixed(2)}|${(assessed ?? -1).toFixed(2)}|${(paid ?? -1).toFixed(2)}`;
  return { amount, courtKey, signature };
}

/** Pull the most-likely "balance still owed" number from a record's
 *  fields. Backward-compat wrapper around recordBalanceInfo for callers
 *  that don't need the collection signature. */
export function recordBalance(rec: RecordRow): number | null {
  return recordBalanceInfo(rec)?.amount ?? null;
}

/** Build a per-record map identifying which records share a collection
 *  account. For each record with a parseable balance, the map gives:
 *    • signature       - collection-account key
 *    • amount          - the (single) balance for the account
 *    • groupSize       - number of records sharing this signature
 *    • isCollectionGroup - true when groupSize > 1 (the smoking gun for
 *                          MCCS rolling multiple tickets into one balance)
 *
 *  PersonPage uses this to suppress the misleading per-record "$498"
 *  balance chip on each individual ticket inside a collection account,
 *  replacing it with a clearer "part of a $498 collection account" hint.
 *  deriveOpenItems uses it to count each unique signature's balance ONCE
 *  in the headline total. */
export interface RecordCollectionEntry {
  signature: string;
  amount: number;
  groupSize: number;
  isCollectionGroup: boolean;
}
export function buildCollectionMap(records: RecordRow[]): Map<number, RecordCollectionEntry> {
  // Pass 1: count records per signature.
  const sizeBySig = new Map<string, number>();
  const amountBySig = new Map<string, number>();
  for (const r of records) {
    const info = recordBalanceInfo(r);
    if (!info || info.amount <= 0) continue;
    sizeBySig.set(info.signature, (sizeBySig.get(info.signature) ?? 0) + 1);
    amountBySig.set(info.signature, info.amount);
  }
  // Pass 2: emit per-record entry referencing the group size.
  const out = new Map<number, RecordCollectionEntry>();
  for (const r of records) {
    const info = recordBalanceInfo(r);
    if (!info || info.amount <= 0) continue;
    const groupSize = sizeBySig.get(info.signature) ?? 1;
    out.set(r.id, {
      signature: info.signature,
      amount: amountBySig.get(info.signature) ?? info.amount,
      groupSize,
      isCollectionGroup: groupSize > 1,
    });
  }
  return out;
}

/** True when this record's data indicates an active outstanding warrant.
 *  The site stores "Y"/"Yes"/"Outstanding" in a Warrant or Warrant
 *  Outstanding field.
 *
 *  Accepts either a RecordRow (we'll dig into r.data) or a RecordData
 *  directly. Both call sites are valid: deriveOpenItems iterates RecordRow,
 *  while ActiveWarrantsBanner has already type-narrowed to RecordData when
 *  it calls in.
 *
 *  This is the SINGLE source of truth for client-side warrant detection.
 *  ActiveWarrantsBanner imports this - do NOT duplicate the regex/field
 *  walking logic anywhere else. (The server side has its own copy in
 *  recordHasWarrantRaw; if you change behavior here, mirror there.) */
export function recordHasActiveWarrant(input: RecordRow | RecordData): boolean {
  // Narrow to RecordData. RecordRow has .data; RecordData has .summary.
  let data: RecordData;
  if ("data" in input) {
    if (!isRecordData(input.data)) return false;
    data = input.data;
  } else if ("summary" in input && typeof (input as RecordData).summary === "object") {
    data = input as RecordData;
  } else {
    return false;
  }
  // Look in summary AND subPages - the field can appear on either depending
  // on whether the View Additional Details capture succeeded.
  const fieldBags: Array<Record<string, string | string[]>> = [
    data.summary,
    ...((data.subPages ?? []).map((sp) => sp.fields).filter(Boolean) as Array<Record<string, string | string[]>>),
  ];
  for (const bag of fieldBags) {
    for (const k of Object.keys(bag)) {
      if (!/\bwarrant\b/i.test(k)) continue;
      const v = pickStr(bag, k);
      // Y / Yes / Outstanding all signal active warrant. Recalled / N / No
      // do not. The status text "Recalled Warrant" handles itself via
      // isOpenStatus returning false.
      if (/^\s*Y\b|yes|outstanding/i.test(v) && !/recalled/i.test(v)) {
        return true;
      }
    }
  }
  // Also check the top-level Status - some rows have status="Active Warrant"
  // without a separate Warrant field.
  const status = recordStatus(data.summary) ?? "";
  if (/active\s+warrant|^warrant/i.test(status) && !/recalled/i.test(status)) {
    return true;
  }
  return false;
}

/** Local alias used by deriveOpenItems below - kept named the way the rest
 *  of this file already calls it. */
const recordHasWarrant = recordHasActiveWarrant;

/** Short identifier for a record - case number if available, else the
 *  ordinal/offense fallback. Used in the line-item detail. */
function recordLabel(rec: RecordRow): string {
  if (rec.case_number) return rec.case_number;
  if (!isRecordData(rec.data)) return `Record #${rec.ordinal}`;
  const c = pickStr(rec.data.summary, "Prefix/Year/Number") || pickStr(rec.data.summary, "Case");
  return c || `Record #${rec.ordinal}`;
}

/** Court name (already-prettified is fine for our use case - strip prefix
 *  number, leave the rest). */
function recordCourt(rec: RecordRow): string {
  if (!isRecordData(rec.data)) return "";
  const raw = pickStr(rec.data.summary, "Court");
  if (!raw) return "";
  return raw.replace(/^\d+\s*-\s*/, "").trim();
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers - external-lookup parsing
// ─────────────────────────────────────────────────────────────────────────

/** Safe-parse the raw_json of an external lookup. Returns null on any
 *  failure so the caller can fall back to "unknown amount" rendering. */
function safeParseExternal(row: ExternalLookup | null | undefined): Record<string, unknown> | null {
  if (!row || row.status !== "completed" || !row.raw_json) return null;
  try {
    return JSON.parse(row.raw_json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Date helpers
// ─────────────────────────────────────────────────────────────────────────

function relDays(days: number): string {
  if (days < 0) return `${-days}d ago`;
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days < 7) return `in ${days}d`;
  if (days < 30) return `in ${Math.round(days / 7)}w`;
  if (days < 365) return `in ${Math.round(days / 30)}mo`;
  return `in ${Math.round(days / 365)}y`;
}

function shortDate(iso: string): string {
  // Defensive - DB rows occasionally have unexpected types; string-coerce
  // before calling .split so we never throw at render time.
  const safe = typeof iso === "string" ? iso : "";
  const parts = safe.split("-").map((n) => parseInt(n, 10));
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return safe;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Main derivation
// ─────────────────────────────────────────────────────────────────────────

export interface OpenItemsInput {
  records: RecordRow[];
  upcomingDates: UpcomingCourtDate[];
  /** Latest per-source map. Keys we read: "mvc-surcharge", "mvc-restoration".
   *  Other sources are ignored here - their UI lives in the external panel. */
  externalLatest: Record<string, ExternalLookup | null>;
}

export function deriveOpenItems({
  records,
  upcomingDates,
  externalLatest,
}: OpenItemsInput): OpenItemsResult {
  const items: OpenItem[] = [];
  let totalDollars = 0;
  let hasUnknownAmounts = false;

  // ── Open MCCS cases - fines + warrants ──────────────────────────────
  // We split into two distinct items: (1) aggregated fines across all
  // open cases, (2) one item per active warrant (warrants demand
  // case-level attention). This avoids burying a warrant inside a vague
  // "5 open cases" lump.
  const safeRecords = Array.isArray(records) ? records : [];
  const openRecords = safeRecords.filter((r) => {
    if (!r || !isRecordData(r.data)) return false;
    return isOpenStatus(recordStatus(r.data.summary));
  });

  // Group balances by collection-account signature. Each unique signature
  // contributes its balance ONCE to the total - even if 30 ticket rows
  // all show the same $498 rolled-up Balance Due (Tarquino's parking-
  // collection case). recordCount counts the underlying records;
  // accountCount counts the deduplicated balances that actually need to
  // be paid. Without this we'd over-report by 30× on collection accounts.
  const balanceGroups = new Map<string, { amount: number; recordCount: number }>();
  let mccsFinesUnknown = 0;
  for (const r of openRecords) {
    const info = recordBalanceInfo(r);
    if (info === null) { mccsFinesUnknown++; continue; }
    if (info.amount <= 0) continue;
    const ex = balanceGroups.get(info.signature);
    if (ex) ex.recordCount++;
    else balanceGroups.set(info.signature, { amount: info.amount, recordCount: 1 });
  }
  const mccsFinesTotal = Array.from(balanceGroups.values()).reduce((a, g) => a + g.amount, 0);
  const mccsFinesAccountCount = balanceGroups.size;
  const mccsFinesRecordCount = Array.from(balanceGroups.values())
    .reduce((a, g) => a + g.recordCount, 0);
  // A "rolled-up collection" exists when at least one group has multiple
  // records - the situation we're correcting for here. Used in the
  // detail text so paralegals know why ticket-count >> account-count.
  const hasCollectionRollup = Array.from(balanceGroups.values()).some((g) => g.recordCount > 1);
  if (mccsFinesAccountCount > 0) {
    let detail: string;
    if (mccsFinesRecordCount === 1) {
      detail = "across 1 open case";
    } else if (mccsFinesAccountCount === 1 && hasCollectionRollup) {
      // Single rolled-up collection: 30 tickets, 1 actual balance.
      detail = `1 collection account · ${mccsFinesRecordCount} ticket${mccsFinesRecordCount === 1 ? "" : "s"} rolled up`;
    } else if (hasCollectionRollup) {
      // Mix: some standalone cases, some rolled-up collections.
      detail = `${mccsFinesAccountCount} balance${mccsFinesAccountCount === 1 ? "" : "s"} across ${mccsFinesRecordCount} cases (collections rolled up)`;
    } else {
      detail = `across ${mccsFinesRecordCount} open cases`;
    }
    items.push({
      kind: "mccs-fines",
      label: "MCCS time-payment balances",
      value: `$${mccsFinesTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      detail,
      link: { kind: "open-records" },
      urgency: mccsFinesTotal >= 1000 ? "high" : "med",
      amount: mccsFinesTotal,
    });
    totalDollars += mccsFinesTotal;
  } else if (mccsFinesUnknown > 0 && openRecords.length > 0) {
    // We have open cases but no parseable balance. Still surface them so
    // staff knows there's outstanding work; just mark the amount unknown.
    items.push({
      kind: "mccs-fines",
      label: "Outstanding MCCS cases",
      value: `${openRecords.length}`,
      detail: "balance amounts not on file",
      link: { kind: "open-records" },
      urgency: "med",
      amount: null,
    });
    hasUnknownAmounts = true;
  }

  // Warrants - each gets its own item so the case is identifiable.
  for (const r of openRecords) {
    if (!recordHasWarrant(r)) continue;
    const court = recordCourt(r);
    items.push({
      kind: "mccs-warrant",
      label: "Active warrant",
      value: recordLabel(r),
      detail: court || undefined,
      link: { kind: "record", recordId: r.id },
      urgency: "high",
      amount: null,
    });
  }

  // ── MVC Surcharge - public lookup, often the biggest debt ────────────
  const surcharge = safeParseExternal(externalLatest["mvc-surcharge"]);
  if (surcharge) {
    const owed = surcharge.totalOwed;
    const noMatch = Boolean(surcharge.noMatch);
    if (typeof owed === "number" && owed > 0) {
      const lineCount = Array.isArray(surcharge.lines) ? surcharge.lines.length : 0;
      items.push({
        kind: "mvc-surcharge",
        label: "NJ MVC surcharge debt",
        value: `$${owed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        detail: lineCount > 0 ? `${lineCount} violation${lineCount === 1 ? "" : "s"}` : "outstanding balance",
        link: { kind: "mvc-source", source: "mvc-surcharge" },
        urgency: owed >= 1000 ? "high" : "med",
        amount: owed,
      });
      totalDollars += owed;
    } else if (owed === null && !noMatch) {
      // Page rendered but our parser couldn't find an amount. Surface so
      // staff knows to read the screenshot.
      items.push({
        kind: "mvc-surcharge",
        label: "NJ MVC surcharge",
        value: "amount unparsed",
        detail: "see surcharge card below",
        link: { kind: "mvc-source", source: "mvc-surcharge" },
        urgency: "med",
        amount: null,
      });
      hasUnknownAmounts = true;
    }
  }

  // ── MVC Restoration fee - usually $100 base, smaller dollar value but
  //    legally REQUIRED before license restoration regardless of size.
  const restoration = safeParseExternal(externalLatest["mvc-restoration"]);
  if (restoration) {
    const due = restoration.totalDue;
    const failed = Boolean(restoration.loginFailed);
    if (typeof due === "number" && due > 0) {
      items.push({
        kind: "mvc-restoration",
        label: "NJ MVC restoration fee",
        value: `$${due.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        detail: "required before license can be restored",
        link: { kind: "mvc-source", source: "mvc-restoration" },
        urgency: "med",
        amount: due,
      });
      totalDollars += due;
    } else if (due === null && !failed) {
      items.push({
        kind: "mvc-restoration",
        label: "NJ MVC restoration fee",
        value: "amount unparsed",
        detail: "see restoration card below",
        link: { kind: "mvc-source", source: "mvc-restoration" },
        urgency: "low",
        amount: null,
      });
      hasUnknownAmounts = true;
    }
  }

  // ── Upcoming court dates - first date is featured in the headline; the
  //    next ~3 also appear in the line-item list so they're scannable.
  const datesByUrgency = (d: UpcomingCourtDate): ItemUrgency =>
    d.daysOut <= 7 ? "high" : d.daysOut <= 30 ? "med" : "low";

  const safeDates = Array.isArray(upcomingDates) ? upcomingDates : [];
  for (const d of safeDates.slice(0, 4)) {
    if (!d) continue;
    const days = typeof d.daysOut === "number" ? d.daysOut : 0;
    items.push({
      kind: d.kind === "payment" ? "payment-due" : "court-date",
      label: d.kind === "payment" ? "Payment due" : "Court appearance",
      value: relDays(days),
      detail: `${shortDate(String(d.date ?? ""))} · ${String(d.description ?? "")}`.slice(0, 110),
      link: typeof d.recordId === "number" ? { kind: "record", recordId: d.recordId } : null,
      urgency: datesByUrgency({ ...d, daysOut: days }),
      amount: null,
    });
  }

  // ── Sort: high urgency first, then within urgency by amount desc, then
  //    by label so deterministic order. We don't sort the upcoming-dates
  //    items by amount (they have none); they fall through to the label
  //    sort and end up after the financial items naturally.
  const URGENCY_RANK: Record<ItemUrgency, number> = { high: 0, med: 1, low: 2 };
  items.sort((a, b) => {
    if (URGENCY_RANK[a.urgency] !== URGENCY_RANK[b.urgency]) {
      return URGENCY_RANK[a.urgency] - URGENCY_RANK[b.urgency];
    }
    if ((b.amount ?? 0) !== (a.amount ?? 0)) {
      return (b.amount ?? 0) - (a.amount ?? 0);
    }
    return a.label.localeCompare(b.label);
  });

  return {
    totalDollars,
    hasUnknownAmounts,
    itemCount: items.length,
    items,
    nextCourtDate: upcomingDates[0] ?? null,
  };
}
